// Wire identity and credential fields remain in the canonical request but
// never become editable presentation fields or expose protocol identifiers.
export function privateActionField(key: string): boolean {
  const words = key
    .replace(/([a-z0-9])([A-Z])/gu, '$1_$2')
    .toLowerCase()
    .split('_');
  return (
    words.some((word) =>
      [
        'id',
        'ids',
        'hash',
        'digest',
        'token',
        'secret',
        'password',
        'credential',
        'authorization',
      ].includes(word),
    ) || ['apikey', 'api_key', 'toolname', 'tool_name', 'replaces'].includes(key.toLowerCase())
  );
}

/** Credential-like keys: never shown at any depth, whatever title a provider gives them. */
export function credentialField(key: string): boolean {
  const words = key
    .replace(/([a-z0-9])([A-Z])/gu, '$1_$2')
    .toLowerCase()
    .split(/[_-]/u);
  return (
    words.some((word) =>
      ['token', 'secret', 'password', 'credential', 'authorization', 'cookie'].includes(word),
    ) || key.toLowerCase().replace(/[_-]/gu, '') === 'apikey'
  );
}

// Envelope identities (who acts, which company, which connection, which account): never shown,
// whatever title a provider gives them. Compared without case, underscores or hyphens.
const ENVELOPE = new Set([
  'connectionid',
  'organizationid',
  'orgid',
  'companyid',
  'accountid',
  'actorid',
  'userid',
  'owneruserid',
]);
export const envelopeField = (key: string) => ENVELOPE.has(key.toLowerCase().replace(/[_-]/gu, ''));

/** The value without any private key at any depth, as the existing card's fields do. */
export function publicValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(publicValue);
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !privateActionField(key) && !credentialField(key) && !envelopeField(key))
        .map(([key, child]) => [key, publicValue(child)]),
    );
  return value;
}

/**
 * The top-level arguments a person reads on an approval surface. A provider resource the server
 * titles (roomId → "Room") is shown even though its key ends in Id; credentials and envelope
 * identities never are. Untitled keys keep the shared id/credential rule. Nested values go
 * through `publicValue`.
 */
export function visibleFields(
  values: Record<string, unknown>,
  titles: Record<string, string> = {},
): Array<[string, unknown]> {
  return Object.entries(values).filter(([key, value]) => {
    if (value === null || value === undefined || value === '') return false;
    if (credentialField(key) || envelopeField(key)) return false;
    return Boolean(titles[key]) || !privateActionField(key);
  });
}
